import {
    DAuditInfo,
    IAuditStamps,
    RhCheckbox,
    RhFormWrapper,
    RhNumberField,
    RhTabsWrapper,
    RHTextField,
} from '../../../../../dum';
import React, { useMemo } from 'react';
import * as yup from 'yup';
import { SchemaOf } from 'yup';
import {
    ActivityEntityName,
    useUpsertResourceDialogMutation,
} from '../../../../../../services';
import { useHistorialTab } from '../../../../historial';
import {
    FIELD_LABELS,
    shortLabel,
    transformToNumber,
} from '../../../../../../helpers';
import { ResourceCategoryInput } from './resource-category-id/resource-category-id-input';

export interface IResource {
    id?: number | null;
    name: string;
    include_units_in_summary: boolean;
    resource_category_id?: number | null;
    current_group_weight?: number | null;
    current_unit_price?: number | null;
    current_group_price?: number | null;
    unit_price_name?: string | null;
}

interface IResourceForm {
    // Audit stamps ride alongside the form values — display only, never
    // submitted, and deliberately kept out of IResource (see IAuditStamps).
    resource?: (IResource & IAuditStamps) | null;
    onSuccess: () => void;
    disabled?: boolean;
}

export function ResourceForm({
    resource,
    onSuccess,
    disabled = false,
}: IResourceForm) {
    const [mutation, { loading }] = useUpsertResourceDialogMutation();

    const historialTab = useHistorialTab({
        entityName: ActivityEntityName.Resource,
        entityId: resource?.id,
        unsavedMessage:
            'El historial aparece una vez que el material ha sido guardado.',
        emptyMessage: 'No hay actividades registradas para este material.',
    });

    const initialValues = useMemo<IResource>(() => {
        return {
            id: resource?.id || undefined,
            name: resource?.name || '',
            resource_category_id: resource?.resource_category_id || null,
            include_units_in_summary:
                resource?.include_units_in_summary || false,
            current_group_price: resource?.current_group_price || null,
            current_group_weight: resource?.current_group_weight || null,
            current_unit_price: resource?.current_unit_price || null,
            unit_price_name: resource?.unit_price_name || '',
        };
    }, [resource]);

    const validationSchema: SchemaOf<IResource> = yup.object({
        id: yup.number().notRequired().nullable(),
        name: yup.string().required(`'Nombre' es obligatorio`),
        resource_category_id: yup
            .number()
            .transform(transformToNumber)
            .required(`'Categoria' es obligatorio`)
            .nullable(),
        current_group_weight: yup
            .number()
            .transform(transformToNumber)
            .notRequired()
            .nullable(),
        current_group_price: yup
            .number()
            .transform(transformToNumber)
            .notRequired()
            .nullable(),
        current_unit_price: yup
            .number()
            .transform(transformToNumber)
            .notRequired()
            .nullable(),
        unit_price_name: yup.string().notRequired().nullable(),
        include_units_in_summary: yup.boolean().required(),
    });

    return (
        <RhFormWrapper
            validationSchema={validationSchema}
            initialValues={initialValues}
            disabled={loading || disabled}
            actionBarStart={
                resource?.id ? (
                    <DAuditInfo
                        created_by={resource.created_by}
                        updated_by={resource.updated_by}
                        createdLabel={'Creado por'}
                        updatedLabel={'Actualizado por'}
                    />
                ) : null
            }
            onSubmit={async (data) => {
                try {
                    await mutation({
                        variables: {
                            ResourceUpsertInput: {
                                id: resource?.id || null,
                                name: data.name,
                                resource_category_id:
                                    data.resource_category_id || null,
                                include_units_in_summary:
                                    data.include_units_in_summary,
                                current_group_weight:
                                    data.current_group_weight || 0,
                                current_group_price:
                                    data.current_group_price || 0,
                                current_unit_price:
                                    data.current_unit_price || 0,
                                unit_price_name: data.unit_price_name || null,
                                group_weight_strict: 0,
                            },
                        },
                    });
                    onSuccess();
                } catch (e) {
                    console.error(e);
                }
            }}
            onInvalid={async (errors) => {
                console.error(errors);
            }}
        >
            <RhTabsWrapper
                tabs={[
                    {
                        label: 'General',
                        fields: ['name', 'resource_category_id'],
                        content: (
                            <>
                                <RHTextField
                                    name={'name'}
                                    label={shortLabel(FIELD_LABELS.name)}
                                />
                                <ResourceCategoryInput />
                            </>
                        ),
                    },
                    {
                        label: 'Configuración',
                        optional: true,
                        fields: [
                            'include_units_in_summary',
                        ],
                        content: (
                            <>
                                <RhCheckbox
                                    name={'include_units_in_summary'}
                                    label={shortLabel(
                                        FIELD_LABELS.include_units_in_summary,
                                    )}
                                />
                            </>
                        ),
                    },
                    {
                        label: 'Precios',
                        optional: true,
                        fields: [
                            'current_group_weight',
                            'current_group_price',
                            'current_unit_price',
                            'unit_price_name',
                        ],
                        content: (
                            <>
                                <RhNumberField
                                    name={'current_group_weight'}
                                    label={shortLabel(
                                        FIELD_LABELS.current_group_weight,
                                    )}
                                />
                                <RhNumberField
                                    name={'current_group_price'}
                                    label={shortLabel(
                                        FIELD_LABELS.current_group_price,
                                    )}
                                />
                                <RhNumberField
                                    name={'current_unit_price'}
                                    label={shortLabel(
                                        FIELD_LABELS.current_unit_price,
                                    )}
                                />
                                <RHTextField
                                    name={'unit_price_name'}
                                    label={shortLabel(
                                        FIELD_LABELS.unit_price_name,
                                    )}
                                />
                            </>
                        ),
                    },
                    // Empty for non-admins, so they get no tab at all.
                    ...historialTab,
                ]}
            />
        </RhFormWrapper>
    );
}
